import { Button, Heading } from '@chakra-ui/react';
import {
  ActionButtons,
  Modal,
  deleteQuery,
  getLeadData,
  pushToDataLayer,
  setQuery,
} from '@repo/shared/shared';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { FC } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { UserAgreementFields } from '@/widgets/UserAgreementFields';
import { leadModel } from '@/entities/lead';
import { getValidationMessages } from '@/shared/constants';
import { usePageName } from '@/shared/hooks';
import { useGlobalState } from '@/shared/providers';
import { EmailField, InputField, PhoneField, TextareaField } from '@/shared/ui';
import type { ButtonProps } from '@chakra-ui/react';

interface FormData {
  companyName: string;
  name: string;
  email: string;
  phone: string;
  description: string;
  isConsent: boolean;
}

interface IRequestModalProps extends ButtonProps {
  buttonText?: string;
  description?: string;
}

const RequestModal: FC<IRequestModalProps> = ({
  buttonText,
  description = '',
  ...rest
}) => {
  const { t } = useTranslation();
  const messages = getValidationMessages(t);
  const queryName = 'req_access';
  const router = useRouter();
  const isOpen = router.query?.[queryName] === 'true';
  const { isMobile } = useGlobalState();
  const pageName = usePageName();

  const validation = {
    minLength: {
      value: 2,
      message: messages.minCharacters(2),
    },
    maxLength: {
      value: 20,
      message: messages.maxCharacters(20),
    },
  };

  const methods = useForm({
    reValidateMode: 'onBlur',
    mode: 'onBlur',
    defaultValues: {
      companyName: '',
      name: '',
      email: '',
      phone: '',
      description: '',
      isConsent: false,
    } satisfies FormData,
  });

  const onCloseModal = () => {
    deleteQuery(router, queryName);
  };

  const onOpen = () => {
    setQuery({ router, queryName, val: 'true' });
  };

  const { action: upsertAppLead, isLoading } = leadModel.useUpsertAppLead(onCloseModal);

  const onSubmit = ({ isConsent: _, ...formData }: FormData) => {
    pushToDataLayer('created_crm_lead', { from_page: pageName });
    const leadData = getLeadData(true);

    upsertAppLead({
      input: {
        ...leadData,
        ...formData,
        description: `${formData.description} ${description}`.trim(),
      },
    });
  };

  const isConsent = methods.watch('isConsent');

  return (
    <>
      <Button
        gridArea='button'
        variant={{ base: 'link', md: 'outline' }}
        onClick={onOpen}
        {...rest}
      >
        {buttonText || t('modals.requestAccess.buttonText')}
      </Button>

      <Modal
        contentProps={{ overflow: 'scroll' }}
        isMobile={isMobile}
        isOpen={isOpen}
        onClose={onCloseModal}
      >
        <Heading
          size='lg'
          textAlign='center'
        >
          {t('modals.requestAccess.heading')}
        </Heading>
        <FormProvider {...methods}>
          <form
            noValidate
            style={{ marginTop: '1rem' }}
            onSubmit={methods.handleSubmit(onSubmit)}
          >
            <InputField
              isRequired
              label={t('modals.requestAccess.nameLabel')}
              name='name'
              placeholder={t('modals.requestAccess.namePlaceholder')}
              validationParameters={validation}
            />
            <EmailField
              validationParameters={{
                validate: (value: string, formData: FormData) => {
                  if (formData?.phone) {
                    return true;
                  }
                  return !!value || messages.requiredField(t('common.form.email'));
                },
              }}
              onChange={() => methods.trigger('phone')}
            />
            <PhoneField
              validationParameters={{
                validate: (value: string, formData: FormData) => {
                  if (formData?.email) {
                    return true;
                  }
                  return !!value || messages.requiredField(t('common.form.phone'));
                },
              }}
              onChange={() => methods.trigger('email')}
            />
            <InputField
              isRequired
              label={t('modals.requestAccess.companyLabel')}
              name='companyName'
              placeholder={t('modals.requestAccess.companyPlaceholder')}
              validationParameters={validation}
            />
            <TextareaField
              label={t('modals.requestAccess.commentsLabel')}
              name='description'
              placeholder={t('modals.requestAccess.commentsPlaceholder')}
            />
            <UserAgreementFields>
              <ActionButtons
                isSubmitting={isLoading}
                mt='0'
                negativeButtonProps={{ w: 'full' }}
                positiveButtonProps={{ type: 'submit', isDisabled: !isConsent, w: 'full' }}
                positiveButtonText={t('modals.requestAccess.sendButton')}
                onCancel={onCloseModal}
              />
            </UserAgreementFields>
          </form>
        </FormProvider>
      </Modal>
    </>
  );
};

export default RequestModal;
